import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import RoadmapCalendar from './RoadmapCalendar';
import RoadmapControls from './RoadmapControls';
import RoadmapCriticalEmptyState from './RoadmapCriticalEmptyState';
import RoadmapEmptyState from './RoadmapEmptyState';
import RoadmapFilteredEmptyState from './RoadmapFilteredEmptyState';
import RoadmapList from './RoadmapList';
import RoadmapPdfModal from './RoadmapPdfModal';
import RoadmapRowBar from './RoadmapRowBar';
import RoadmapSkeleton from './RoadmapSkeleton';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import ProjectRoadmapContext from 'Context/project_plan/ProjectRoadmapContext';

import CalendarIcon from 'Assets/icons/calendar.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import StarFilledIcon from 'Assets/icons/star-filled.svg';

// Chips drawn with the same treatment as the calendar's pills.
const LEGEND_CHIP = 'tw-inline-flex tw-items-center tw-gap-1 tw-h-7 tw-px-2.5 tw-rounded-md tw-border tw-border-s-[3px] tw-text-xs tw-font-medium';

// Critical path chips show while the result on screen was filtered by it.
const RoadmapLegend = ({ schedule }) => (
    <div className="tw-hidden md:tw-flex tw-items-center tw-gap-x-2">
        {/* While filtered by the critical path no pill is blue or green: key pills turn solid red or amber and complete milestones are hidden. */}
        {!schedule && (
            <span className={classNames(LEGEND_CHIP, 'tw-bg-blue tw-border-blue tw-text-white')}>
                <StarFilledIcon className="tw-h-3 tw-w-3" aria-hidden="true" />
                {$t('Key milestone')}
            </span>
        )}
        <span className={classNames(LEGEND_CHIP, 'tw-bg-white tw-border-blue tw-text-charcoal')}>{$t('Milestone')}</span>
        {!schedule && <span className={classNames(LEGEND_CHIP, 'tw-bg-green-25 tw-border-green tw-text-dark-green')}>{$t('Complete')}</span>}
        {schedule && <span className={classNames(LEGEND_CHIP, 'tw-bg-red-10 tw-border-red tw-text-charcoal')}>{$t('Critical')}</span>}
        {schedule?.mode === 'near' && (
            <span className={classNames(LEGEND_CHIP, 'tw-bg-amber-50 tw-border-amber-500 tw-text-charcoal')}>{$t('Near-critical')}</span>
        )}
    </div>
);

export default function RoadmapIndex() {
    const { settings, data, loading, error, retry, savedAt, update, expandAll, collapseAll, resetFilters, goToToday, pdfDownloading } =
        useContext(ProjectRoadmapContext);

    const [showPdfModal, setShowPdfModal] = useState(false);

    const hasVisibleMilestones = !!data?.tree?.sources?.length;
    const isCalendar = settings.style === 'calendar';

    const retryButton = (
        <TextButton variant="primary" onClick={retry}>
            {$t('Retry')}
        </TextButton>
    );

    const errorMessage = (
        <p className="tw-flex tw-items-center tw-justify-center tw-gap-x-1.5 tw-py-16 tw-text-sm tw-text-charcoal-75">
            {$t('An error occurred, please try again')}
            {retryButton}
        </p>
    );
    // A failed refetch leaves the previous result on screen; say it no longer matches the controls.
    const staleErrorMessage = (
        <p role="alert" className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-mb-3 tw-text-sm tw-text-red">
            {$t("The view couldn't be refreshed. Showing the previous result.")}
            {retryButton}
        </p>
    );

    let content;
    if (!data) {
        content = error ? errorMessage : <RoadmapSkeleton />;
    } else if (!data.hasAnyMilestones) {
        content = <RoadmapEmptyState />;
    } else if (!hasVisibleMilestones && data.schedule) {
        content = (
            <RoadmapCriticalEmptyState
                noTarget={data.schedule.target_id === null}
                onResetFilters={resetFilters}
                onTurnOff={() => update({ criticalPath: 'off' })}
            />
        );
    } else if (!hasVisibleMilestones) {
        content = <RoadmapFilteredEmptyState onReset={resetFilters} />;
    } else {
        content = isCalendar ? <RoadmapCalendar /> : <RoadmapList />;
    }

    return (
        <div className="table__card tw-flex tw-flex-col tw-space-y-6">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <h4 className="table__header">{$t('Milestone Roadmap')}</h4>

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2">
                    <RoadmapLegend schedule={data?.schedule} />

                    {isCalendar && hasVisibleMilestones && (
                        <Button variant="secondary" small onClick={goToToday}>
                            <CalendarIcon className="tw-h-4 tw-w-4 tw-me-1.5" aria-hidden="true" />
                            {$t('Go to today')}
                        </Button>
                    )}

                    <Button variant="primary" small onClick={() => setShowPdfModal(true)} disabled={pdfDownloading || !data} loading={pdfDownloading}>
                        <DownloadIcon className="tw-h-4 tw-w-4 tw-me-1.5" aria-hidden="true" />
                        {$t('Download PDF')}
                    </Button>
                </div>
            </div>

            {(!data || data.hasAnyMilestones) && (
                <RoadmapControls
                    settings={settings}
                    periodYears={data?.periodYears || []}
                    filterOptions={data?.filterOptions}
                    schedule={data?.schedule}
                    onChange={update}
                />
            )}

            {data?.hasAnyMilestones && (
                <RoadmapRowBar
                    showPillText={isCalendar}
                    pill={settings.pill}
                    savedAt={savedAt}
                    onExpandAll={expandAll}
                    onCollapseAll={collapseAll}
                    onPillChange={(pill) => update({ pill })}
                />
            )}

            {/* The previous result stays on screen while a refetch is in flight. */}
            <div className={classNames('tw-transition-opacity', { 'tw-opacity-60': loading && !!data })} aria-busy={loading}>
                {error && data && staleErrorMessage}
                {content}
            </div>

            {showPdfModal && createPortal(<RoadmapPdfModal onClose={() => setShowPdfModal(false)} />, document.body)}
        </div>
    );
}
